import React, { useCallback, useEffect, useState } from "react";
import api from "./api.js";
import { hhConfirm } from "./ConfirmDialog.jsx";
import UserList from "./UserList.jsx";
import UserForm from "./UserForm.jsx";

const EMPTY_FORM = {
  email: "",
  name: "",
  password: "",
  role: "employee",
  employee_id: "",
};

export default function UsersModal({ onClose, employees = [], currentUserId }) {
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [err, setErr] = useState("");
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load users");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      if (editingId) {
        const payload = {
          name: form.name,
          role: form.role,
          employee_id: form.employee_id || null,
        };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editingId}`, payload);
      } else {
        await api.post("/users", {
          email: form.email.toLowerCase().trim(),
          password: form.password,
          name: form.name,
          role: form.role,
          employee_id: form.employee_id || null,
        });
      }
      setForm(EMPTY_FORM);
      setEditingId(null);
      await load();
    } catch (ex) {
      const detail = ex?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  function startEdit(u) {
    setEditingId(u.id);
    setForm({
      email: u.email,
      name: u.name,
      password: "",
      role: u.role,
      employee_id: u.employee_id || "",
    });
    setErr("");
  }

  function cancelEdit() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setErr("");
  }

  function remove(u) {
    hhConfirm(
      `Delete user ${u.email}? This cannot be undone.`,
      async () => {
        try {
          await api.delete(`/users/${u.id}`);
          await load();
        } catch (ex) {
          const detail = ex?.response?.data?.detail;
          setErr(typeof detail === "string" ? detail : "Delete failed");
        }
      },
      { danger: true, confirmLabel: "Delete user" },
    );
  }

  return (
    <div
      className="modal-bg"
      style={{ display: "flex" }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="users-modal"
    >
      <div className="modal" style={{ width: 760, maxWidth: "95vw" }}>
        <div className="modal-head">
          <h3>
            <i className="ti ti-users" style={{ marginRight: 6 }}></i>
            User Management
          </h3>
          <button
            className="btn btn-sm"
            style={{ color: "#fff", borderColor: "rgba(255,255,255,0.3)" }}
            onClick={onClose}
            data-testid="users-modal-close"
          >
            <i className="ti ti-x"></i>
          </button>
        </div>
        <div className="modal-body">
          <div className="form-section-title">
            <i className="ti ti-list" style={{ fontSize: 12 }}></i> Existing Users
          </div>
          <UserList
            users={users}
            currentUserId={currentUserId}
            onEdit={startEdit}
            onDelete={remove}
          />
          <UserForm
            form={form}
            setForm={setForm}
            editingId={editingId}
            busy={busy}
            err={err}
            employees={employees}
            onSubmit={submit}
            onCancelEdit={cancelEdit}
          />
        </div>
      </div>
    </div>
  );
}
